import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Users } from "lucide-react";
import useSettings from "../../hooks/useSettings";

import GrantAccessModal from "./components/GrantAccessModal";
import RemoveAccessModal from "./components/RemoveAccessModal";

const ProjectAccess = () => {
  const { projects, loadProjectsWithAccess } = useSettings();

  // ─────────────────────────────
  // State
  // ─────────────────────────────

  const [expandedProject, setExpandedProject] = useState(null);

  const [showGrantModal, setShowGrantModal] = useState(false);

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);

  // ─────────────────────────────
  // Load project access data
  // ─────────────────────────────

  useEffect(() => {
    loadProjectsWithAccess();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
  if (projects && projects.length > 0) {
    console.log("FULL PROJECT:", projects[0]);
  }
}, [projects]);
  // ─────────────────────────────
  // Open Grant Modal
  // ─────────────────────────────

  const handleGrantAccess = (project) => {
    setSelectedProject(project);
    setShowGrantModal(true);
  };

  // ─────────────────────────────
  // Open Remove Modal
  // ─────────────────────────────

  const handleRemoveAccess = (project, user) => {
    setSelectedProject(project);
    setSelectedUser(user);
    setShowRemoveModal(true);
  };
  const getInitials = (name = "") => name.trim().charAt(0).toUpperCase() || "?";

const getProfileImageUrl = (user) => {
  if (!user) return "";
  if (typeof user.profilePicture === "string") return user.profilePicture;
  if (user.profilePicture?.url) return user.profilePicture.url;
  if (typeof user.avatar === "string") return user.avatar;
  if (typeof user.profileImage === "string") return user.profileImage;
  return "";
};

  return (
    <>
      <div className="mt-5 space-y-3 relative">
        {/* Project Cards */}
        {projects?.map((project) => (
          <div key={project._id} className="bg-white rounded-xl p-5">
            {/* Top Section */}
            <div
              className="flex flex-wrap justify-between items-center gap-2 cursor-pointer"
              onClick={() =>
                setExpandedProject(
                  expandedProject === project._id ? null : project._id,
                )
              }
            >
              {/* Project Name */}
              <h2 className="text-[16px]">{project.name}</h2>

              <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                {/* Number of resources */}
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Users size={16} />
                  {project.members?.length || 0} Resources
                </div>

                {/* Start Date */}

                <div className="text-sm text-gray-400">
                  Started On{" "}
                  {(() => {
                    const raw = project.startDate || project.createdAt;
                    const d = raw ? new Date(raw) : null;
                    return d && !isNaN(d.getTime())
                      ? d.toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—";
                  })()}
                </div>

                {expandedProject === project._id ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </div>
            </div>

            {/* Expanded Table */}

            {expandedProject === project._id && (
              <div className="mt-6 overflow-x-auto overflow-y-auto max-h-[420px] rounded-lg">
                <table className="w-full min-w-[640px] lg:min-w-0">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="text-gray-400 text-sm text-left border-b">
                      <th className="pb-3">Resource</th>

                      <th>Role</th>

                      <th>Specialization</th>

                      <th>Assigned On</th>

                      <th className="text-center">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {project.members?.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center py-5 text-gray-400"
                        >
                          No members assigned
                        </td>
                      </tr>
                    ) : (
                      project.members?.map((user) => (
                        <tr key={user._id} className="border-b">
                          {/* User */}
                          <td className="py-4">
                            <div className="flex items-center gap-3">
   <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-500 overflow-hidden shrink-0">
  {getProfileImageUrl(user) ? (
    <img
      src={getProfileImageUrl(user)}
      alt={user.name}
      className="w-full h-full object-cover"
    />
  ) : (
    getInitials(user.name)
  )}
</div>
                              <div>
                                <p className="text-sm font-medium">
                                  {user.name}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Role */}
                          <td className="py-4">
                            <p className="text-sm text-gray-700">{user.role}</p>
                          </td>

                          {/* Specialization */}
                          <td className="py-4">
                            <div className="flex gap-2 flex-wrap">
                              {user.specialization?.map((skill) => (
                                <span
                                  key={skill}
                                  className="px-2 py-1 text-[10px] rounded-full bg-blue-100 text-blue-600 uppercase"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </td>

                          {/* Assigned Date */}
                          <td className="py-4">
                            <p className="text-sm text-gray-700">
                              {new Date(
                                user.assignedOn ||
                                  user.assignedAt ||
                                  user.createdAt,
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </p>
                          </td>

                          {/* Action */}
                          <td className="py-4 text-center">
                            <button
                              onClick={() => handleRemoveAccess(project, user)}
                              className="px-4 py-1 text-red-400 text-sm bg-red-100 rounded hover:bg-red-200 transition"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Grant Access Button */}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => handleGrantAccess(project)}
                    className="
                        px-6 py-2
                        rounded-lg
                        border
                        border-[#4C5CE7]
                        text-[#4C5CE7]
                        text-sm
                        hover:bg-[#4C5CE7]
                        hover:text-white
                        transition
                      "
                  >
                    + Grant Access
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Grant Access Modal */}
      {showGrantModal && (
        <GrantAccessModal
          project={selectedProject}
          onClose={() => setShowGrantModal(false)}
        />
      )}

      {/* Remove Access Modal */}
      {showRemoveModal && (
        <RemoveAccessModal
          project={selectedProject}
          user={selectedUser}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </>
  );
};

export default ProjectAccess;
